import { useState } from 'react';
import Switch from '@mui/material/Switch';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import { Colors } from '../config/default';
import { HtmlTooltip } from '../services/common';
import { SAVING, SAVED, SAVE_FAILED } from '../hooks/useSettingsAutosave';
import { Profile } from '../constants/appConstants';

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#fff'
  },
  '& .MuiSwitch-track': {
    backgroundColor: Colors.RED,
    transparency: 1
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: Colors.TOURQUISE
  }
}));

// The outcome of this switch's own write.
//
// Nothing at all when there is no status: this component is also the plain
// on/off switch in the role editor, which never writes on its own and would
// otherwise carry a strip of empty space next to every permission in a
// two-column grid. Nothing needs reserving on the settings page either — the
// status is the last thing in the row, so nothing shifts when it comes and goes.
const SaveStatus = ({ status }) => {
  if (!status) return null;
  return (
    <Box
      sx={{
        ml: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: '11px'
      }}>
      {status === SAVING && <CircularProgress size={11} sx={{ color: Colors.TEXTGREY }} />}
      {status === SAVED && (
        <>
          <CheckRounded sx={{ fontSize: '13px', color: '#22c55e' }} />
          <Typography sx={{ fontSize: '11px', color: '#22c55e' }}>{Profile.SAVED}</Typography>
        </>
      )}
      {status === SAVE_FAILED && (
        <>
          <ErrorOutlineRounded sx={{ fontSize: '13px', color: '#f87171' }} />
          <Typography sx={{ fontSize: '11px', color: '#f87171' }}>{Profile.NOT_SAVED}</Typography>
        </>
      )}
    </Box>
  );
};

export default function SwitchComponent({
  description,
  checked,
  setChecked,
  disable,
  disabledReason,
  tooltip,
  message,
  width,
  status
}) {
  // A disabled MUI switch takes no pointer events, so a click on it lands on the
  // wrapper below instead — which is the only chance to answer the question the
  // click was asking. Nothing moved and nothing was said is how a locked toggle
  // gets reported as a setting that would not save.
  const [reasonOpen, setReasonOpen] = useState(false);
  const locked = disable && !!disabledReason;

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        marginTop: '.3rem',
        display: 'inline-flex',
        alignItems: 'center'
      }}>
      <Grid item style={{ width: width || '250px' }}>
        {tooltip ? (
          <HtmlTooltip
            title={
              <Typography color="inherit" sx={{ fontSize: '1em' }}>
                {message}
              </Typography>
            }>
            <Typography sx={{ color: Colors.WHITE, fontSize: '14px' }}>{description}</Typography>
          </HtmlTooltip>
        ) : (
          <Typography sx={{ color: Colors.WHITE, fontSize: '14px' }}>{description}</Typography>
        )}
      </Grid>
      <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
        <HtmlTooltip
          open={locked && reasonOpen}
          onOpen={() => setReasonOpen(true)}
          onClose={() => setReasonOpen(false)}
          disableHoverListener={!locked}
          disableFocusListener={!locked}
          disableTouchListener={!locked}
          title={
            <Typography color="inherit" sx={{ fontSize: '1em' }}>
              {disabledReason}
            </Typography>
          }>
          <Box
            component="span"
            onClick={() => locked && setReasonOpen(true)}
            sx={{ display: 'inline-flex', cursor: locked ? 'help' : 'default' }}>
            <StyledSwitch
              disabled={disable}
              checked={checked ?? false}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </Box>
        </HtmlTooltip>
        <SaveStatus status={status} />
      </Grid>
    </Grid>
  );
}
